import { useState } from 'react';
import { loginPassword, requestPasswordReset, resetPassword, startGoogleOAuth } from '../lib/api';
import { LOGO_URL } from '../lib/config';
import { useApp } from '../app/AppContext';

function EyeButton({ on, toggle }: { on: boolean; toggle: () => void }) {
  return (
    <button type="button" className="pw-toggle" onClick={toggle} aria-label={on ? 'Hide password' : 'Show password'} title={on ? 'Hide password' : 'Show password'}>
      {on
        ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
    </button>
  );
}

export function Login() {
  const { reload } = useApp();
  const [logoOk, setLogoOk] = useState(true);
  const [mode, setMode] = useState<'signin' | 'forgot'>('signin');
  const [showPw, setShowPw] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(sessionStorage.getItem('scb.loginError'));
  sessionStorage.removeItem('scb.loginError');
  // forgot state
  const [fStep, setFStep] = useState<'email' | 'reset'>('email');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await loginPassword(email.trim(), password);
      const denied = await reload();
      if (denied) { sessionStorage.removeItem('scb.loginError'); setErr(denied); }
    }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Sign-in failed'); }
    finally { setBusy(false); }
  }

  async function requestReset(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try { await requestPasswordReset(email.trim()); setMsg('If that email has an account, we sent a 6-digit reset code.'); setFStep('reset'); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Could not request a reset'); }
    finally { setBusy(false); }
  }
  async function doReset(e: React.FormEvent) {
    e.preventDefault(); setErr(null);
    if (newPw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await resetPassword(email.trim(), code.trim(), newPw);
      setMode('signin'); setShowPw(true); setMsg('Password reset. You can sign in now.'); setPassword(''); setCode(''); setNewPw('');
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Could not reset password'); }
    finally { setBusy(false); }
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        {logoOk
          ? <img className="logo" src={LOGO_URL} alt="Stray Cat Blues" onError={() => setLogoOk(false)} />
          : <div className="fallback" style={{ fontSize: '1.4rem' }}>🐾 Stray Cat Blues</div>}
        <h1>{mode === 'forgot' ? 'Reset your password' : 'Donated Items Shop'}</h1>
        <p className="lead">{mode === 'forgot' ? 'For email & password accounts.' : 'Sign in to browse and reserve available donations.'}</p>

        {err && <div className="notice err">{err}</div>}
        {msg && <div className="notice ok">{msg}</div>}

        {mode === 'forgot' ? (
          fStep === 'email' ? (
            <form className="stack" onSubmit={requestReset} style={{ textAlign: 'left' }}>
              <label className="field">Email<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
              <button className="btn" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send reset code'}</button>
              <button type="button" className="btn-link" onClick={() => { setMode('signin'); setErr(null); setMsg(null); }}>Back to sign in</button>
            </form>
          ) : (
            <form className="stack" onSubmit={doReset} style={{ textAlign: 'left' }}>
              <label className="field">Reset code<input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="123456" required /></label>
              <label className="field">New password
                <span className="pw-wrap">
                  <input type={pwVisible ? 'text' : 'password'} autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
                  <EyeButton on={pwVisible} toggle={() => setPwVisible((v) => !v)} />
                </span>
              </label>
              <button className="btn" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Resetting…' : 'Reset password'}</button>
              <button type="button" className="btn-link" onClick={() => { setMode('signin'); setErr(null); setMsg(null); }}>Back to sign in</button>
            </form>
          )
        ) : (
          <>
            <button className="gbtn" onClick={startGoogleOAuth}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4H24v7.6h12.6c-.3 2.1-1.6 5.3-4.7 7.4l7.2 5.6c4.3-4 6.9-9.9 6.9-16.6z" />
                <path fill="#FBBC05" d="M10.3 28.6A14.4 14.4 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.7l7.8-6.1z" />
                <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.2-5.6c-2 1.4-4.6 2.3-8.7 2.3-6.4 0-11.8-3.7-13.7-9l-7.8 6.1C6.4 42.6 14.6 48 24 48z" />
              </svg>
              Sign in with Google
            </button>
            <div className="divider">straycatblues.org accounts</div>
            {!showPw ? (
              <button className="btn-link" onClick={() => setShowPw(true)}>Use an email &amp; password instead</button>
            ) : (
              <form className="stack" onSubmit={submit} style={{ textAlign: 'left' }}>
                <label className="field">Email
                  <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </label>
                <label className="field">Password
                  <span className="pw-wrap">
                    <input type={pwVisible ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <EyeButton on={pwVisible} toggle={() => setPwVisible((v) => !v)} />
                  </span>
                </label>
                <button className="btn" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
                <button type="button" className="btn-link" onClick={() => { setMode('forgot'); setFStep('email'); setErr(null); setMsg(null); }}>Forgot password?</button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
